import * as React from "react";
import {CSSProperties, useState} from "react";
import {backend} from "../../../../../xconvert-backend";
import {authentication} from "../../../../../authentication";
import {Button, Dropdown, Icon, Loader, Table} from "semantic-ui-react";
import {FailsWithPipeline, Pipeline, PipelineTestDetailsByCommit} from "./PipelineStatusModel";
import {formatDate} from "../../../../../format";
import AceEditor from "react-ace";
import {Splitter} from "../../../../util/table-splitter-ui/src/components/Splitters/Splitter";

export function PipelineStatusPage() {

    const [groupedByCommit, setGroupedByCommit] = React.useState<PipelineTestDetailsByCommit[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [expandedRows, setExpandedRows] = useState<string[]>([])

    const [displayText, setDisplayText] = React.useState("")
    const [displayObject, setDisplayObject] = React.useState<any>("")

    const [selectedCommit, setSelectedCommit] = React.useState<string | null>(null)
    const [selectedTest, setSelectedTest] = React.useState<string | null>(null)
    const [selectedType, setSelectedType] = React.useState<"Commit" | "Test" | "Job" | null>(null)
    const [stackTraceMode, setStackTraceMode] = React.useState(false)
    const [selectedProject, setSelectedProject] = React.useState<string>("ALL_PROJECTS")

    const [latestFinishedPipeline, setLatestFinishedPipeline] = React.useState<Pipeline>(null)

    let selectedRowStyle = {
        backgroundColor: "rgba(0, 175, 236, 0.16)",
        boxShadow: "inset 0 0 0 1px rgba(0, 175, 236, 0.4)"
    } as CSSProperties
    let expandedCommitRowStyle = {
        backgroundColor: "rgba(2, 132, 199, 0.08)",
        boxShadow: "inset 4px 0 0 rgba(2, 132, 199, 0.7)"
    } as CSSProperties

    React.useEffect(() => {
        callPipelineTestDetails().then(r => {
        });
    }, []);

    React.useEffect(() => {
        setStackTraceMode(false)
    }, [displayObject]);

    React.useEffect(() => {
        setExpandedRows([])
        setSelectedCommit(null)
    }, [selectedProject]);

    async function callPipelineTestDetails() {
        let auth = backend.withTokenAuthHeader(authentication.token)
        let response = await fetch('/internalMonitoring/getPipelineTestDetails', {
            method: 'GET',
            headers: {
                "Authorization": auth.headers.Authorization.toString(),
                "Content-Type": "application/json; charset=utf-8"
            }
        })

        if (response.status == 200) {
            let tests = await response.json();
            console.log("[PipelineStatusPage:callPipelineTestDetails] tests: ", tests)
            let grouped = groupByCommit(tests.failsWithCausingCommits);
            console.log("[PipelineStatusPage:callPipelineTestDetails] grouped: ", grouped)

            setGroupedByCommit(grouped)
            setLatestFinishedPipeline(tests.currentPipeline)
        }

        setLoading(false)

    }

    function cleanTestSuiteName(testSuiteName: string) {
        return testSuiteName.replace("test:", "");
    }

    function groupByCommit(tests: FailsWithPipeline[]): PipelineTestDetailsByCommit[] {

        let grouped: PipelineTestDetailsByCommit[] = []

        tests.forEach((test: FailsWithPipeline) => {

            let found = grouped.find(group =>
                group.causingCommitId === test.causingCommit?.id
            );
            if (!found) {

                let newGroup = {
                    causingCommitId: test.causingCommit?.id,
                    causingCommit: test.causingCommit,
                    fails: [test]
                } as PipelineTestDetailsByCommit
                grouped.push(newGroup)
            } else {
                found.fails.push(test)
            }
        })

        console.log("[PipelineStatusPage:groupByCommit] grouped: ", grouped)
        return grouped;


    }

    function normalizeProjectName(projectName: string): string {
        const normalized = projectName.toLowerCase()
        if (normalized === "convert") {
            return "converter"
        }
        if (normalized === "internal-monitoring" || normalized === "external-monitoring") {
            return "monitoring"
        }
        return normalized
    }

    function formatProjectLabel(projectName: string): string {
        return projectName
            .split("-")
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(" ")
    }

    function extractProjectsFromText(value?: string | null): string[] {
        if (!value) {
            return []
        }

        const lower = value.toLowerCase()
        const collected = new Set<string>()
        const knownProjectTokens = [
            "backend",
            "converter",
            "convert",
            "monitoring",
            "internal-monitoring",
            "external-monitoring",
            "api-gateway",
            "ftp",
            "scheduler",
            "geocoding",
            "sms",
            "ocr",
            "vrp",
            "webapp",
            "notification-worker",
            "eta-calculator"
        ]

        const xconvertMatches = lower.match(/xconvert-[a-z0-9-]+/g) || []
        xconvertMatches.forEach(match => {
            collected.add(normalizeProjectName(match.replace("xconvert-", "")))
        })

        knownProjectTokens.forEach(token => {
            if (lower.includes(token)) {
                collected.add(normalizeProjectName(token))
            }
        })

        return Array.from(collected)
    }

    function getProjectsForFail(fail: FailsWithPipeline): string[] {
        const projectSet = new Set<string>()

        const candidates = fail.failedTest
            ? [
                fail.failedTest.testSuite?.name,
                fail.failedTest.testName,
                fail.failedTest.testCase?.classname,
                fail.failedTest.testCase?.file
            ]
            : [
                fail.failedJob?.jobName,
                fail.failedJob?.job?.name,
                fail.failedJob?.job?.stage,
                fail.failedJob?.job?.ref
            ]

        candidates.forEach(candidate => {
            extractProjectsFromText(candidate).forEach(project => projectSet.add(project))
        })

        if (projectSet.size === 0) {
            projectSet.add("unknown")
        }

        return Array.from(projectSet)
    }

    function getProjectsForCommit(commit: PipelineTestDetailsByCommit): string[] {
        const projectSet = new Set<string>()
        commit.fails.forEach(fail => {
            getProjectsForFail(fail).forEach(project => projectSet.add(project))
        })
        return Array.from(projectSet).sort()
    }

    function getCommitKey(commit: PipelineTestDetailsByCommit, index: number): string {
        return commit.causingCommitId || commit.causingCommit?.id || `commit-${index}`
    }

    const projectOptions = React.useMemo(() => {
        const allProjects = new Set<string>()
        groupedByCommit.forEach(commit => {
            commit.fails.forEach(fail => {
                getProjectsForFail(fail).forEach(project => allProjects.add(project))
            })
        })
        return Array.from(allProjects).sort()
    }, [groupedByCommit]);

    function failMatchesSelectedProject(fail: FailsWithPipeline): boolean {
        if (selectedProject === "ALL_PROJECTS") {
            return true
        }
        return getProjectsForFail(fail).includes(selectedProject)
    }

    const filteredGroupedByCommit = React.useMemo(() => {
        return groupedByCommit
            .map(commit => ({
                ...commit,
                fails: commit.fails.filter(fail => failMatchesSelectedProject(fail))
            }))
            .filter(commit => commit.fails.length > 0)
    }, [groupedByCommit, selectedProject]);

    function tableView() {
        function statusChip(label: string, bg: string, color: string = "#0f172a") {
            return <span style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "28px",
                borderRadius: "999px",
                padding: "2px 10px",
                background: bg,
                color: color,
                fontWeight: 700,
                fontSize: "12px"
            }}>{label}</span>
        }

        function toggleCommitRow(rowId: string, commit: PipelineTestDetailsByCommit) {
            const isRowCurrentlyExpanded = expandedRows.includes(rowId);
            const newExpandedRows = isRowCurrentlyExpanded
                ? expandedRows.filter(id => id !== rowId)
                : expandedRows.concat(rowId);

            setExpandedRows(newExpandedRows);
            setDisplayObject(commit)
            setDisplayText(JSON.stringify(commit, null, 4))
            setSelectedCommit(rowId)
            setSelectedType("Commit")
        }

        function buildRunnerList(commit: PipelineTestDetailsByCommit): string {
            let runnersList: string[] = []
            commit.fails.forEach(fail => {
                let r = fail.failedTest?.testSuite.gitlabRunner
                if (r && runnersList.indexOf(r) === -1) {
                    runnersList.push(r)
                }
            })
            return runnersList.join(", ")
        }

        function failedTestsCard(commit: PipelineTestDetailsByCommit, commitKey: string) {
            const failedTests = commit.fails
                .filter(fail => fail.failedTest != null)
                .map(fail => fail.failedTest);

            if (failedTests.length === 0) {
                return null
            }

            return <div style={{
                border: "1px solid #dbeafe",
                borderRadius: "10px",
                background: "#f8fbff",
                padding: "12px"
            }}>
                <div style={{display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px"}}>
                    <Icon name={"warning sign"} color={"orange"}/>
                    <strong>Failed tests</strong>
                    {statusChip(String(failedTests.length), "rgba(251, 191, 36, 0.25)", "#78350f")}
                </div>
                <Table compact={"very"} basic={"very"} celled>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Module</Table.HeaderCell>
                            <Table.HeaderCell>Execution time</Table.HeaderCell>
                            <Table.HeaderCell>Class name</Table.HeaderCell>
                            <Table.HeaderCell>Test name</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {failedTests.map((test: any) =>
                            <Table.Row
                                key={"row-expanded-test-" + commitKey + "_" + test.testName}
                                style={selectedTest === test.testName ? selectedRowStyle : {}}
                                onClick={() => {
                                    setDisplayObject(test.testCase)
                                    setDisplayText(JSON.stringify(test.testCase, null, 4))
                                    setSelectedTest(test.testName)
                                    setSelectedType("Test")
                                }}
                            >
                                <Table.Cell>{cleanTestSuiteName(test.testSuite.name)}</Table.Cell>
                                <Table.Cell>{test.testCase.execution_time}</Table.Cell>
                                <Table.Cell>{test.testCase.classname}</Table.Cell>
                                <Table.Cell>{test.testName}</Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </div>
        }

        function failedJobsCard(commit: PipelineTestDetailsByCommit, commitKey: string) {
            const failedJobs = commit.fails
                .filter(fail => fail.failedJob != null)
                .map(fail => fail.failedJob);

            if (failedJobs.length === 0) {
                return null
            }

            return <div style={{
                border: "1px solid #dbeafe",
                borderRadius: "10px",
                background: "#f8fbff",
                padding: "12px"
            }}>
                <div style={{display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px"}}>
                    <Icon name={"settings"} color={"blue"}/>
                    <strong>Failed jobs</strong>
                    {statusChip(String(failedJobs.length), "rgba(191, 219, 254, 0.55)", "#0c4a6e")}
                </div>
                <Table compact={"very"} basic={"very"} celled>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Job</Table.HeaderCell>
                            <Table.HeaderCell>Duration</Table.HeaderCell>
                            <Table.HeaderCell>Stage</Table.HeaderCell>
                            <Table.HeaderCell>Runner</Table.HeaderCell>
                            <Table.HeaderCell>Failure reason</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {failedJobs.map((job: any) =>
                            <Table.Row
                                key={"row-expanded-job-" + commitKey + "_" + job.jobName}
                                style={selectedTest === job.jobName ? selectedRowStyle : {}}
                                onClick={() => {
                                    setDisplayObject(job.job)
                                    setDisplayText(JSON.stringify(job.job, null, 4))
                                    setSelectedTest(job.jobName)
                                    setSelectedType("Job")
                                }}
                            >
                                <Table.Cell>{job.jobName}</Table.Cell>
                                <Table.Cell>{job.job.duration}</Table.Cell>
                                <Table.Cell>{job.job.stage}</Table.Cell>
                                <Table.Cell>{job.job.runner?.description}</Table.Cell>
                                <Table.Cell>{job.job.failure_reason}</Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </div>
        }

        function commitRow(commit: PipelineTestDetailsByCommit, index: number) {
            const commitKey = getCommitKey(commit, index)
            const isExpanded = expandedRows.includes(commitKey)
            const date = commit.causingCommit != null
                ? formatDate(new Date(commit.causingCommit.committed_date))
                : "unknown"
            const runners = buildRunnerList(commit)
            const projects = getProjectsForCommit(commit)

            return <Table.Row
                key={"row-data-" + commitKey}
                onClick={() => toggleCommitRow(commitKey, commit)}
                style={{
                    ...(isExpanded ? expandedCommitRowStyle : {}),
                    ...(selectedCommit === commitKey ? selectedRowStyle : {})
                }}
            >
                <Table.Cell collapsing>
                    <Button
                        basic
                        circular
                        icon={isExpanded ? "chevron down" : "chevron right"}
                        size={"mini"}
                        onClick={(event) => {
                            event.stopPropagation()
                            toggleCommitRow(commitKey, commit)
                        }}
                    />
                </Table.Cell>
                <Table.Cell>{statusChip(String(commit.fails.length), "rgba(254, 226, 226, 0.8)", "#7f1d1d")}</Table.Cell>
                <Table.Cell>
                    <div style={{display: "flex", flexWrap: "wrap", gap: "6px"}}>
                        {projects.map(project =>
                            <React.Fragment key={project}>
                                {statusChip(formatProjectLabel(project), "rgba(226, 232, 240, 0.7)", "#1e293b")}
                            </React.Fragment>
                        )}
                    </div>
                </Table.Cell>
                <Table.Cell>{commit.causingCommit?.author_name}</Table.Cell>
                <Table.Cell>{date}</Table.Cell>
                <Table.Cell>{commit.causingCommit?.last_pipeline?.id}</Table.Cell>
                <Table.Cell>{runners || "-"}</Table.Cell>
                <Table.Cell>{commit.causingCommit?.message}</Table.Cell>
            </Table.Row>
        }

        function expandedCommitRow(commit: PipelineTestDetailsByCommit, index: number) {
            const commitKey = getCommitKey(commit, index)
            if (!expandedRows.includes(commitKey)) {
                return null
            }

            const testsCard = failedTestsCard(commit, commitKey)
            const jobsCard = failedJobsCard(commit, commitKey)

            return <Table.Row key={"row-expanded-parent-" + commitKey}>
                <Table.Cell colSpan={8} style={{background: "rgba(226, 232, 240, 0.28)", padding: "14px 16px"}}>
                    <div style={{display: "grid", gridTemplateColumns: "1fr", gap: "12px"}}>
                        {testsCard}
                        {jobsCard}
                        {!testsCard && !jobsCard &&
                            <div style={{
                                border: "1px dashed #cbd5e1",
                                borderRadius: "10px",
                                padding: "12px",
                                color: "#64748b"
                            }}>
                                No failed tests or failed jobs available for this commit.
                            </div>
                        }
                    </div>
                </Table.Cell>
            </Table.Row>
        }

        let allItemRows: JSX.Element[] = []
        filteredGroupedByCommit.forEach((commit, index) => {
            allItemRows.push(commitRow(commit, index))
            const expandedRow = expandedCommitRow(commit, index)
            if (expandedRow) {
                allItemRows.push(expandedRow)
            }
        })

        const dropdownOptions = [
            {key: "ALL_PROJECTS", text: "All projects", value: "ALL_PROJECTS"},
            ...projectOptions.map(project => ({
                key: project,
                text: formatProjectLabel(project),
                value: project
            }))
        ]

        return <div style={{display: "flex", flexDirection: "column", gap: "10px"}}>
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "10px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                background: "#f8fafc"
            }}>
                <strong>Project filter:</strong>
                <Dropdown
                    selection
                    options={dropdownOptions}
                    value={selectedProject}
                    onChange={(event, data) => setSelectedProject(String(data.value))}
                    style={{minWidth: "220px"}}
                />
                <span style={{color: "#64748b", fontSize: "13px"}}>
                    {filteredGroupedByCommit.length} commit groups shown
                </span>
            </div>

            <div style={{
                overflow: "auto",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                background: "#ffffff",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)"
            }}>
                <Table selectable basic={"very"} celled>
                    <Table.Header>
                        <Table.Row style={{background: "#f8fafc"}}>
                            <Table.HeaderCell collapsing></Table.HeaderCell>
                            <Table.HeaderCell>Failures</Table.HeaderCell>
                            <Table.HeaderCell>Projects</Table.HeaderCell>
                            <Table.HeaderCell>Committed by</Table.HeaderCell>
                            <Table.HeaderCell>Committed at</Table.HeaderCell>
                            <Table.HeaderCell>Pipeline</Table.HeaderCell>
                            <Table.HeaderCell>Runners</Table.HeaderCell>
                            <Table.HeaderCell>Message</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>{allItemRows}</Table.Body>
                </Table>
            </div>
        </div>
    }

    function gitlabButton(url: string) {
        return <Button
            width={50}
            color={"orange"}
            onClick={() => window.open(url, "_blank")}
            icon={"gitlab"}
        />
    }

    function buildCommitButtons() {
        return <div style={{
            display: "flex",
            flexDirection: "row",
        }}>
            <h1>Selected Commit:</h1>
            {gitlabButton(displayObject.causingCommit.web_url)}
        </div>
    }

    function buildTestButtons() {
        return <div style={{
            display: "flex",
            flexDirection: "row",
        }}>
            <h1>Selected Test:</h1>
            {!stackTraceMode && <Button
                color={"red"}
                icon={"error"}
                onClick={() => {
                    setDisplayText(displayObject.system_output)
                    setStackTraceMode(true)
                }
                }
            >Show Stack-Trace
            </Button>}
            {stackTraceMode && <Button
                color={"green"}
                icon={"check"}
                onClick={() => {
                    setDisplayText(JSON.stringify(displayObject, null, 4))
                    setStackTraceMode(false)
                }
                }>Show complete Object
            </Button>
            }

        </div>
    }

    function displayTextArea() {

        return <>
            {selectedType === "Commit" && buildCommitButtons()}
            {selectedType === "Test" && buildTestButtons()}
            <AceEditor
                theme={"monokai"}
                style={{flex: 1}}
                mode={"json"}
                value={displayText}
                width={"100%"}
            />
        </>
    }

    return <>
        <h1>Failing Pipeline Tests: {latestFinishedPipeline &&
            <a href={latestFinishedPipeline?.webUrl}><Icon name={"gitlab"}/>{latestFinishedPipeline?.id}</a>}
        </h1>

        <Loader active={loading}/>
        <Splitter
            name={"PipelineStatusPage"}
            left={tableView()}
            right={displayTextArea()}
        />
    </>
}